// =====================================================================
// src/modulos/flats.js — a aba "Flats" da imobiliária (0101)
//
// Os flats DELA agrupados por proprietário, o dono de cada um trocado no
// próprio card, e o botão "Proprietários" que abre o cadastro dos donos.
// Hugo, 02/11/2026: o cadastro do dono é da imobiliária ("dashboard e
// aba"); o hotel deixa de ver.
//
// DE ONDE VEIO O DESENHO, do MdM (index.html):
//   · `renderFlats` (10485): o topo `display:flex;justify-content:
//     space-between;align-items:center;margin-bottom:20px;flex-wrap:wrap;
//     gap:10px` com o `<h1>` 22px 700 #111827 "Flats (N)" e, à direita, o
//     botão btn-secondary "[ic-flats] Proprietários"; uma seção por
//     proprietário com a barra azul 4×22 #1B3A7A, o `<h2>` 17px 700 #1B3A7A
//     e "(N flats)" 12px #6b7280; e a grade `repeat(auto-fill,minmax(220px,
//     1fr))` gap 10px de cards.
//   · O CARD de cada flat não está nos trechos do MdM que eu tenho: ele vai
//     no padrão do card de quarto do Dashboard daqui (`modulos/dashboard.js`,
//     que copiou `dashboardSection`, 1997) — "Quarto N" 15px 700 #111827 —,
//     e o seletor no padrão do "Bloqueio manual" do detalhe do quarto
//     (form-row, label 11px, select.input 12px).
//
// ONDE DIVERGE, E POR QUÊ:
//   · Lá o dono do flat se troca no formulário do flat (com foto, valores,
//     condomínio…). Aqui o flat é do hotel — a imobiliária não cadastra
//     flat, só diz de quem é: o seletor "Proprietário" fica no card.
//   · Os outros botões do topo de lá (novo flat, importar) não existem: o
//     quarto é criado pelo hotel em Configurações.
//   · "Quarto N", não "Flat N" (plano da 0101); o grupo "Sem proprietário"
//     no fim, para o flat que ainda não tem dono no cadastro.
//
// QUEM VÊ O QUÊ é do banco: a RLS de `unidades` e o corte por
// `imobiliaria_id` dão só os flats dela; `proprietarios_da_imobiliaria()`
// só os donos dela; `definir_proprietario_do_flat` recusa flat ou dono de
// outra imobiliária, e dono desativado.
// =====================================================================
import { el, icone, aviso, enquantoAtualiza } from '../ui.js';
import { gruposDosFlats, opcoesDoSeletor, valorDoSeletor, avisoDaTroca, podeVerProprietarios, podeEditarProprietarios } from '../lib/proprietarios.js';
import { abrirProprietarios } from './proprietarios.js';

const ic = (nome, w) => { const i = icone(nome); Object.assign(i.style, { width: w + 'px', height: w + 'px', verticalAlign: '-3px', marginRight: '6px' }); return i; };

export function montar(cont, { auth, contexto }) {
  let vivo = true;
  let unidades = [], proprietarios = [];
  const imob = contexto.imobiliaria && contexto.imobiliaria.id;
  const edita = podeEditarProprietarios(contexto);

  if (!imob || !podeVerProprietarios(contexto)) {
    cont.dataset.tela = 'flats';
    cont.appendChild(el('p', { class: 'vazio' }, 'Os flats e os proprietários são da imobiliária: só quem entra pela imobiliária vê esta aba.'));
    return () => { vivo = false; };
  }

  async function carregar() {
    const [u, p] = await Promise.all([
      auth.ler('unidades', `select=id,numero,andar,imobiliaria_id,proprietario_id,ativo&cliente_id=eq.${contexto.cliente.id}&imobiliaria_id=eq.${imob}&ativo=eq.true&order=numero`),
      auth.rpc('proprietarios_da_imobiliaria', {}),
    ]);
    unidades = u || []; proprietarios = p || [];
  }
  function recarregar() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }

  function seletor(q) {
    const atual = q.proprietario_id || '';
    if (!edita) {
      const nome = (proprietarios.find(p => p.id === q.proprietario_id) || {}).nome;
      return el('div', { style: { fontSize: '12px', color: nome ? '#374151' : '#9ca3af', marginTop: '8px' } }, nome || 'Sem proprietário');
    }
    const sel = el('select', { class: 'input', dataset: { proprietarioDoFlat: q.id }, style: { fontSize: '12px', padding: '4px 6px', marginTop: '4px' },
      onchange: async ev => {
        const novo = valorDoSeletor(ev.target.value);
        ev.target.disabled = true;
        try { await auth.rpc('definir_proprietario_do_flat', { p_unidade: q.id, p_proprietario: novo }); }
        catch (e) { aviso(e.message, 'erro'); ev.target.value = atual; ev.target.disabled = false; return; }
        aviso(avisoDaTroca(q.numero, novo ? (proprietarios.find(p => p.id === novo) || {}).nome : null));
        recarregar();
      } },
      opcoesDoSeletor(proprietarios, q.proprietario_id).map(o => el('option', { value: o.valor, selected: o.valor === atual, disabled: o.desabilitado }, o.rotulo)));
    return el('div', { class: 'form-row', style: { margin: '8px 0 0' } },
      el('label', { class: 'label', style: { fontSize: '11px' } }, 'Proprietário'), sel);
  }

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'flats';
    const raiz = el('div', { class: 'fade-in', id: 'flats' });
    // o topo de `renderFlats` (10485)
    raiz.appendChild(el('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' } },
      el('h1', { id: 'flats-titulo', style: { fontSize: '22px', fontWeight: '700', color: '#111827', margin: '0' } }, `Flats (${unidades.length})`),
      el('div', { style: { display: 'flex', gap: '8px' } },
        el('button', { type: 'button', class: 'btn btn-secondary', id: 'btn-proprietarios',
          onclick: () => abrirProprietarios({ auth, contexto, aoMudar: recarregar }) }, ic('ic-flats', 16), 'Proprietários'))));

    const grupos = gruposDosFlats(unidades, proprietarios);
    for (const g of grupos) {
      raiz.appendChild(el('div', { dataset: { grupo: g.titulo }, style: { marginBottom: '24px' } },
        el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' } },
          el('div', { style: { width: '4px', height: '22px', background: '#1B3A7A', borderRadius: '2px' } }),
          el('h2', { style: { fontSize: '17px', fontWeight: '700', color: '#1B3A7A', margin: '0' } }, g.titulo),
          el('span', { style: { fontSize: '12px', color: '#6b7280' } }, `(${g.quartos.length} flat${g.quartos.length !== 1 ? 's' : ''})`)),
        el('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: '10px' } },
          g.quartos.map(q => el('div', { class: 'card', dataset: { flat: q.id }, style: { padding: '14px 16px' } },
            el('div', { style: { fontSize: '15px', fontWeight: '700', color: '#111827' } }, 'Quarto ' + q.numero),
            q.andar != null ? el('div', { style: { fontSize: '12px', color: '#6b7280', marginTop: '2px' } }, `${q.andar}º andar`) : null,
            seletor(q))))));
    }
    if (!grupos.length) raiz.appendChild(el('p', { class: 'vazio' }, 'Nenhum flat da imobiliária neste hotel.'));
    cont.appendChild(raiz);
  }

  recarregar();
  return () => { vivo = false; };
}
